"use client"

import * as React from "react"
import { Bell, X, Trash2 } from "lucide-react"
import { AppNotificationGroup } from "@/components/notifications/AppNotificationGroup"
import { NotificationItem } from "@/types/notification"

interface NotificationCenterPanelProps {
  isOpen: boolean
  onClose: () => void
  notifications: NotificationItem[]
  setNotifications: React.Dispatch<React.SetStateAction<NotificationItem[]>>
  handleGenerateRealNotification?: () => void
}

export function NotificationCenterPanel({
  isOpen,
  onClose,
  notifications,
  setNotifications,
}: NotificationCenterPanelProps) {
  if (!isOpen) return null

  const handleClearAll = () => {
    setNotifications([])
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("bold_token") : null
      fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          query: `mutation { deleteAllNotifications }`
        })
      }).catch(() => {})
    } catch (e) {}
  }

  const handleDeleteItem = (id: number | string) => {
    setNotifications((prev) => prev.filter((item) => item.id !== id))
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("bold_token") : null
      fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          query: `mutation DeleteNotif($id: ID!) { deleteNotification(id: $id) }`,
          variables: { id: String(id) }
        })
      }).catch(() => {})
    } catch (e) {}
  }

  const handleDeleteApp = (appName: string) => {
    setNotifications((prev) => prev.filter((item) => (item.appName || "Sistema") !== appName))
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("bold_token") : null
      fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          query: `mutation DeleteAppNotifs($appName: String!) { deleteAppNotifications(appName: $appName) }`,
          variables: { appName }
        })
      }).catch(() => {})
    } catch (e) {}
  }

  // Agrupar notificações por appName preservando a ordem cronológica
  const groupedByApp = React.useMemo(() => {
    const groups: { appName: string; items: NotificationItem[] }[] = []
    const map = new Map<string, NotificationItem[]>()

    notifications.forEach((item) => {
      const appKey = item.appName || (item.type === "instagram" ? "Instagram" : "Bold Workspace")
      if (!map.has(appKey)) {
        map.set(appKey, [])
        groups.push({ appName: appKey, items: map.get(appKey)! })
      }
      map.get(appKey)!.push(item)
    })

    return groups
  }, [notifications])

  return (
    <>
      {/* Backdrop transparente para fechar ao clicar fora */}
      <div 
        className="fixed inset-0 z-[998]"
        onClick={onClose}
      />

      {/* Painel da Central de Notificações com Fundo 100% Sólido e Animação Suave (duration-500 ease-in-out) */}
      <div className="fixed top-0 right-0 h-full w-[360px] sm:w-[410px] z-[999] bg-white dark:bg-zinc-950 border-l border-zinc-200 dark:border-zinc-800 shadow-2xl transition-all duration-500 ease-in-out animate-in slide-in-from-right-full">
        <div className="relative z-10 flex flex-col h-full w-full p-5 overflow-y-auto select-none font-sans text-zinc-900 dark:text-zinc-100">
          
          {/* Header da Central */}
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center gap-2.5">
              <div className="size-7 rounded-lg bg-[#004EB1]/10 text-[#004EB1] flex items-center justify-center">
                <Bell className="size-4" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-zinc-900 dark:text-zinc-100 uppercase tracking-wider">
                  Central de Notificações
                </h3>
                <span className="text-[10px] text-zinc-400">
                  {notifications.length} {notifications.length === 1 ? "notificação" : "notificações"}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {notifications.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="text-[10px] font-bold text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors cursor-pointer px-2 py-1 bg-red-500/10 dark:bg-red-500/20 rounded-full flex items-center gap-1"
                >
                  <Trash2 className="size-3" />
                  <span>Limpar todas</span>
                </button>
              )}

              <button 
                type="button"
                onClick={onClose}
                className="flex size-7 items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors cursor-pointer"
                title="Fechar"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>

          {/* Lista das Notificações Agrupadas com Animações Suaves */}
          <div className="space-y-6 flex-1 overflow-y-auto pr-1">
            {notifications.length === 0 ? (
              <div className="py-16 flex flex-col items-center justify-center text-center gap-2 text-zinc-400 dark:text-zinc-500">
                <Bell className="size-10 stroke-1 opacity-40" />
                <p className="text-xs font-medium">Nenhuma notificação recente.</p>
              </div>
            ) : (
              groupedByApp.map((group) => (
                <AppNotificationGroup
                  key={group.appName}
                  appName={group.appName}
                  items={group.items}
                  onDelete={handleDeleteItem}
                  onDeleteApp={handleDeleteApp}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </>
  )
}
